import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const FILTERS = ["All", "Founders", "Money", "Product", "Brand", "Code", "Story"];

export default function Mentors() {
  const [mentors, setMentors] = useState([]);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/mentors").then((r) => setMentors(r.data)).catch(() => {});
  }, []);

  const filtered = mentors.filter((m) => {
    const txt = (m.name + m.title + m.domain + m.expertise.join(" ")).toLowerCase();
    if (q && !txt.includes(q.toLowerCase())) return false;
    if (filter === "All") return true;
    const map = { Founders: "Founders", Money: "Personal Finance", Product: "Product", Brand: "Audience", Code: "AI Engineering", Story: "Comms" };
    return m.domain.includes(map[filter]);
  });

  return (
    <div className="min-h-screen mesh-bg pb-32" data-testid="mentors-screen">
      <div className="px-6 pt-12 pb-4">
        <p className="text-xs tracking-widest uppercase text-zinc-500">Marketplace</p>
        <h1 className="font-display text-4xl font-light tracking-tight">Find your mentor.</h1>
        <p className="mt-2 text-sm text-zinc-400">One subscription. Every domain. Always on.</p>
      </div>

      <div className="px-6">
        <div className="glass rounded-full px-4 py-3 flex items-center gap-2">
          <MagnifyingGlass size={16} className="text-zinc-500" />
          <input
            data-testid="mentor-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search mentors, skills…"
            className="bg-transparent flex-1 outline-none text-sm placeholder:text-zinc-600"
          />
        </div>
      </div>

      <div className="mt-4 px-6 flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs border transition-all ${
              filter === f ? "bg-white text-black border-white" : "border-white/10 text-zinc-300"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-5 px-6 grid grid-cols-2 gap-3">
        {filtered.map((m, i) => (
          <motion.div
            key={m.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
          >
            <Link
              to={`/mentors/${m.id}`}
              data-testid={`mentor-card-${m.id}`}
              className="block glass rounded-3xl overflow-hidden active:scale-[0.98] transition"
            >
              <div className="h-40 relative">
                <img src={m.avatar} alt={m.name} className="w-full h-full mentor-portrait" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute top-2 right-2 glass rounded-full px-2 py-1 flex items-center gap-1">
                  <Star size={10} weight="fill" className="text-amber-300" />
                  <span className="text-[10px] font-mono-jb">{m.rating}</span>
                </div>
                <div className="absolute bottom-2 left-3 right-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/70">{m.domain}</div>
                  <div className="text-sm font-medium leading-tight">{m.name}</div>
                  <div className="text-[10px] text-zinc-300/80">{m.title}</div>
                </div>
              </div>
              <div className="p-3 space-y-2">
                <div className="text-[11px] text-zinc-400 line-clamp-2 leading-snug">{m.tagline}</div>
                <div className="flex items-center justify-between">
                  <div className="flex -space-x-2">
                    {m.expertise.slice(0, 2).map((e) => (
                      <span key={e} className="text-[9px] uppercase tracking-wide bg-white/5 border border-white/10 px-2 py-0.5 rounded-full text-zinc-300">{e}</span>
                    ))}
                  </div>
                  <span className="text-[10px] text-zinc-500 font-mono-jb">{(m.learners / 1000).toFixed(1)}k</span>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
